import { http, APIResponse, HttpError } from '@frontend/fetch';
import {
  Merchant,
  PaymentNotificationSettingsResponse,
  PollWorkflowResponse,
  StripeBusinessTypes,
  StripeOnboardingLink,
  UpdateMerchant,
} from './types';

export const LOCATION_ID_HEADER = 'Location-Id';
export const addLocationHeader = (locationId: string, headers: Record<string, string> = {}) => ({
  headers: { [LOCATION_ID_HEADER]: locationId, ...headers },
});

const MERCHANT_NOT_FOUND_CODE_ID = 5;
export const getMerchant = async (locationId: string): Promise<Merchant | undefined> => {
  try {
    const { data } = await http.get<APIResponse<Merchant>>(`payments/v1/merchant`, addLocationHeader(locationId));
    return data;
  } catch (error) {
    if (
      error instanceof HttpError &&
      error.status === 404 &&
      typeof error.data !== 'string' &&
      error.data?.error.codeId === MERCHANT_NOT_FOUND_CODE_ID
    )
      return undefined;
    else throw error;
  }
};

export const getMerchants = async (locationIds: string[]) => {
  // Use a non-null assertion to tell TypeScript this is definitely a string
  const locationId = locationIds?.[0] ?? '';

  return await http.getData<Merchant[]>(`payments/v1/merchants`, {
    ...addLocationHeader(locationId),
    params: { locationIds },
  });
};

type MerchantFormData = {
  merchant: UpdateMerchant;
  logo?: File;
};
export const updateMerchant = async (payload: MerchantFormData, locationId: string): Promise<Merchant> => {
  const formData = new FormData();
  formData.append('merchant-json', JSON.stringify(payload.merchant));

  if (payload.logo) {
    formData.append('merchant-logo', payload.logo);
  }

  const { data } = await http.put<APIResponse<Merchant>>(
    `payments/v1/merchant`,
    formData,
    addLocationHeader(locationId)
  );
  return data;
};

export const getMerchantLogo = async (locationId: string): Promise<any> => {
  return http
    .get(`payments/merchant/logo`, { responseType: 'blob', ...addLocationHeader(locationId) })
    .then((blob) => {
      if (blob instanceof Blob) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      return;
    })
    .then((dataUrl) => {
      return dataUrl;
    });
};

export const createStripeAccount = async (
  businessType: StripeBusinessTypes | string | null,
  newUser: boolean,
  locationId: string
): Promise<any> =>
  await http
    .post<APIResponse<StripeOnboardingLink>>(
      `payments/v1/stripe/account`,
      { newUser, businessType },
      addLocationHeader(locationId)
    )
    .then((data) => data.data);

export const getStripeOnboardingLink = async (
  stripeAccountId: string | undefined,
  refreshURL: string,
  returnURL: string,
  locationId: string
) =>
  await http
    .post<APIResponse<StripeOnboardingLink>>(
      `payments/v1/stripe/onboarding-link`,
      {
        stripeAccountId,
        refreshURL,
        returnURL,
      },
      addLocationHeader(locationId)
    )
    .then((data) => data.data);

export const gqlEndpoint = '/payments/v1/gql/q';

export const setFeatureFlag = (locationId: string, flagName: string, enabled: boolean) =>
  http.post<unknown>(
    `support/v1/featureflags/${locationId}`,
    {
      flagName,
      value: enabled,
    },
    addLocationHeader(locationId)
  );

export const toggleAffirmPaymentMethod = (stripeId: string, enable: boolean, locationId: string) =>
  http.patch<void>(
    `payments/v1/merchant/enable-affirm-payment`,
    {
      stripe_account_id: stripeId,
      enable,
    },
    addLocationHeader(locationId)
  );

export const pollEventWorkflow = async (workflowId: string | null) =>
  await http
    .get<{ data: PollWorkflowResponse; errors: string[] }>(`payments/workflow/${workflowId}`)
    .then(({ data, errors }) => ({ ...data, errors }));

export const getPaymentNotificationSettings = (locationId: string) =>
  http.getData<PaymentNotificationSettingsResponse>(
    `payments/v1/notifications/popup/settings`,
    addLocationHeader(locationId)
  );

export const updatePaymentNotificationSettings = (payload: PaymentNotificationSettingsResponse) =>
  http.post<unknown>(
    `payments/v1/notifications/popup/settings`,
    { ...payload },
    addLocationHeader(payload.location_id)
  );
